import {
  EMPTY_ARRAY,
  getColumnDynamicsState,
  getCustomColDefProperties,
  MixpanelEvent,
  MixpanelEventProperty,
  removeColumn,
  useCallbackRef,
  useDisclosureWithContext,
  useMixpanel,
  type ColumnDynamics,
  type ColumnOrColumnGroup,
} from '@talos/kyoko';
import type { Column as AgGridColumn, ColDef, ColGroupDef, GridApi, IHeaderGroupParams } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { useMarketSelectorAggregationPresets } from '../../../MarketSelector/presets/useMarketSelectorAggregationPresets';
import type { MarketSelectorValue } from '../../../MarketSelector/types';
import { PRICE_GRID_ADD_PRICE_COLUMN_GROUP_ID } from '../../columns/priceGridAddPriceColumn';
import type { PriceGridLadderColumnConfig } from '../../columns/priceGridMarketDataQtyColumns';
import { PriceGridMarketDataColumnGroupHeader } from '../../columns/priceGridSecurityMarketDataColumn';
import { DEFAULT_PRICE_GRID_PRICE_INCREMENT, PriceGridLadderType } from '../../ladder';
import { usePriceGridMarketSelectorItems } from '../usePriceGridMarketSelectorItems';
import {
  PRICE_GRID_LEG_MARKET_DATA_COLUMN_DYNAMICS_SPEC,
  PRICE_GRID_MARKET_DATA_COLUMN_DYNAMICS_SPEC,
} from './constants';
import {
  PriceGridLegMarketDataColumnSettingsDialog,
  PriceGridMarketDataColumnSettingsDialog,
  type PriceGridLegMarketDataColumnSettingsDialogContext,
  type PriceGridMarketDataColumnSettingsDialogContext,
} from './dialogs';
import { getPriceGridLegMarketDataColumn, savePriceGridLegMarketDataColumnSettings } from './legMarketDataColumn';
import { getPriceGridMarketDataColumn, savePriceGridMarketDataColumnSettings } from './marketDataColumn';
import {
  countPriceGridMarketDataColumns,
  getNextPriceGridMarketDataColumnTitle,
} from './priceGridMarketDataColumnHelpers';
import type {
  PriceGridLegMarketDataColumnSettings,
  PriceGridMarketDataColumnHandlers,
  PriceGridMarketDataColumnRow,
  PriceGridMarketDataColumnSettings,
} from './types';
import { buildPriceGridMarketDataColumnColDef, getMarketSelectorItemDisplayName } from './utils';

function isColumnDynamicsForSpec<TSettings>(
  dynamics: ColumnDynamics | undefined,
  spec: string
): dynamics is ColumnDynamics<TSettings> {
  return dynamics?.spec === spec;
}

function getHeaderGroupSettingsColumn(params: IHeaderGroupParams): AgGridColumn | undefined {
  return params.columnGroup.getLeafColumns()[0];
}

function getPriceGridMarketDataColumnGroupDynamics(
  colDef: ColGroupDef
): ColumnDynamics<PriceGridMarketDataColumnSettings> | undefined {
  return colDef.children
    .filter((child): child is ColDef => !('children' in child))
    .map(child => getCustomColDefProperties(child)?.dynamics)
    .find((dynamics): dynamics is ColumnDynamics<PriceGridMarketDataColumnSettings> =>
      isColumnDynamicsForSpec<PriceGridMarketDataColumnSettings>(dynamics, PRICE_GRID_MARKET_DATA_COLUMN_DYNAMICS_SPEC)
    );
}

function buildPriceGridMarketDataColDefOverlay({
  settingsButton,
  onSettingsClick,
}: {
  settingsButton: {
    label: string;
    tooltip?: string;
    showAggregationIcon?: boolean;
  };
  onSettingsClick: (params: IHeaderGroupParams) => void;
}): Partial<ColGroupDef<PriceGridMarketDataColumnRow>> {
  return {
    headerGroupComponent: PriceGridMarketDataColumnGroupHeader,
    headerGroupComponentParams: {
      settingsButton: {
        ...settingsButton,
        onClick: onSettingsClick,
      },
    },
  };
}

function addPriceGridMarketDataColumnBeforeAddButton({
  api,
  dynamics,
  newSettings,
  getColumn,
  getColDefOverlay,
}: {
  api: GridApi;
  dynamics: ColumnDynamics<PriceGridMarketDataColumnSettings>;
  newSettings: PriceGridMarketDataColumnSettings | undefined;
  getColumn: (dynamics: ColumnDynamics<PriceGridMarketDataColumnSettings>) => ColumnOrColumnGroup;
  getColDefOverlay: (
    dynamics: ColumnDynamics<PriceGridMarketDataColumnSettings>
  ) => Partial<ColGroupDef<PriceGridMarketDataColumnRow>>;
}) {
  const columnDefs = api.getColumnDefs();
  if (!columnDefs) {
    return;
  }

  const nextColDef = buildPriceGridMarketDataColumnColDef({
    getColumn,
    dynamics,
    newSettings,
    getColDefOverlay,
  });
  const addColumnIndex = columnDefs.findIndex(
    colDef => 'children' in colDef && colDef.groupId === PRICE_GRID_ADD_PRICE_COLUMN_GROUP_ID
  );
  const nextColumnDefs = [...columnDefs];
  nextColumnDefs.splice(addColumnIndex === -1 ? nextColumnDefs.length : addColumnIndex, 0, nextColDef);
  api.setGridOption('columnDefs', nextColumnDefs);
}

function removePriceGridMarketDataColumn({
  api,
  colId,
  columnGroupId,
  dynamics,
  getColumn,
  getColDefOverlay,
}: {
  api: GridApi;
  colId: string;
  columnGroupId: string;
  dynamics: ColumnDynamics<PriceGridMarketDataColumnSettings>;
  getColumn: (dynamics: ColumnDynamics<PriceGridMarketDataColumnSettings>) => ColumnOrColumnGroup;
  getColDefOverlay: (
    dynamics: ColumnDynamics<PriceGridMarketDataColumnSettings>
  ) => Partial<ColGroupDef<PriceGridMarketDataColumnRow>>;
}) {
  if (dynamics.removable) {
    removeColumn(api, columnGroupId);
    return;
  }

  // Base Price columns are restored from React props, so promote another Price column into this slot before removing it.
  const replacementColumn = api
    .getColumnDefs()
    ?.filter((colDef): colDef is ColGroupDef => 'children' in colDef)
    .map(colDef => ({
      groupId: colDef.groupId,
      dynamics: getPriceGridMarketDataColumnGroupDynamics(colDef),
    }))
    .find(column => column.groupId && column.groupId !== columnGroupId && column.dynamics);

  if (!replacementColumn?.groupId || !replacementColumn.dynamics) {
    return;
  }

  savePriceGridMarketDataColumnSettings({
    api,
    colId,
    dynamics,
    newSettings: getColumnDynamicsState(replacementColumn.dynamics),
    getColumn,
    getColDefOverlay,
  });
  removeColumn(api, replacementColumn.groupId);
}

export function usePriceGridColumnDynamicsSpecs(options?: {
  priceGridMarketDataHandlers?: PriceGridMarketDataColumnHandlers;
  isBBOEnabled: boolean;
  showRFQ: boolean;
  ladderConfig?: PriceGridLadderColumnConfig;
  /** Whether to offer a Leg Price column in the "Add Column" menu, in addition to the plain Price column. */
  supportsLegColumn?: boolean;
}) {
  const {
    isBBOEnabled = false,
    showRFQ = true,
    supportsLegColumn = false,
    priceGridMarketDataHandlers,
    ladderConfig = { ladderType: PriceGridLadderType.Ladder, priceIncrement: DEFAULT_PRICE_GRID_PRICE_INCREMENT },
  } = options ?? {};
  const settingsDialog = useDisclosureWithContext<PriceGridMarketDataColumnSettingsDialogContext>();
  const legSettingsDialog = useDisclosureWithContext<PriceGridLegMarketDataColumnSettingsDialogContext>();
  const { aggregationPresets, aggregationPresetsByID } = useMarketSelectorAggregationPresets();
  const marketSelectorItems = usePriceGridMarketSelectorItems();
  const mixpanel = useMixpanel();

  const marketSelectorItemsByName = useMemo(
    () => new Map(marketSelectorItems.map(item => [item.name, item])),
    [marketSelectorItems]
  );

  const getSelectionDisplayNames = useCallback(
    (selections: string[]) =>
      selections.map(selection => getMarketSelectorItemDisplayName(selection, marketSelectorItemsByName)),
    [marketSelectorItemsByName]
  );

  const getHeaderSettingsButton = useCallback(
    (marketSelectorValue: MarketSelectorValue | undefined) => {
      if (marketSelectorValue?.type === 'preset') {
        return {
          label: aggregationPresetsByID?.get(marketSelectorValue.presetID)?.label ?? marketSelectorValue.presetID,
          tooltip: getSelectionDisplayNames(marketSelectorValue.selections).join(', '),
          showAggregationIcon: true,
        };
      }

      const selections = marketSelectorValue?.selections ?? [];
      if (selections.length === 0) {
        return { label: 'All Markets' };
      }

      const selectionDisplayNames = getSelectionDisplayNames(selections);
      const visibleSelections = selectionDisplayNames.slice(0, 3).join(', ');
      return {
        label: selections.length > 3 ? `${visibleSelections} +${selections.length - 3}` : visibleSelections,
        tooltip: selectionDisplayNames.join(', '),
      };
    },
    [aggregationPresetsByID, getSelectionDisplayNames]
  );

  const getColumn = useCallback(
    (dynamics: ColumnDynamics<PriceGridMarketDataColumnSettings>) =>
      getPriceGridMarketDataColumn(dynamics, priceGridMarketDataHandlers, {
        includeRFQColumn: showRFQ,
        isBBOEnabled,
        ladderConfig,
      }),
    [isBBOEnabled, ladderConfig, priceGridMarketDataHandlers, showRFQ]
  );

  const getLegColumn = useCallback(
    (dynamics: ColumnDynamics<PriceGridLegMarketDataColumnSettings>) =>
      getPriceGridLegMarketDataColumn(dynamics, priceGridMarketDataHandlers, {
        includeRFQColumn: showRFQ,
        isBBOEnabled,
        ladderConfig,
      }),
    [isBBOEnabled, ladderConfig, priceGridMarketDataHandlers, showRFQ]
  );

  const openColumnSettings = useCallbackRef((api: GridApi, column: AgGridColumn, columnGroupId: string) => {
    const dynamics = getCustomColDefProperties(column.getColDef())?.dynamics;
    if (
      !isColumnDynamicsForSpec<PriceGridMarketDataColumnSettings>(dynamics, PRICE_GRID_MARKET_DATA_COLUMN_DYNAMICS_SPEC)
    ) {
      return;
    }

    const colId = column.getColId();

    settingsDialog.open({
      currentSettings: getColumnDynamicsState(dynamics),
      save: newSettings => {
        savePriceGridMarketDataColumnSettings({
          api,
          colId,
          dynamics,
          newSettings,
          getColumn,
          getColDefOverlay: getMarketDataColDefOverlay,
        });
      },
      deleteAction:
        countPriceGridMarketDataColumns(api.getColumnDefs()) > 1
          ? {
              itemName: `price column "${getColumnDynamicsState(dynamics)?.title ?? 'Price'}"`,
              onDelete: () => {
                removePriceGridMarketDataColumn({
                  api,
                  colId,
                  columnGroupId,
                  dynamics,
                  getColumn,
                  getColDefOverlay: getMarketDataColDefOverlay,
                });
              },
            }
          : undefined,
    });
  });

  const openLegColumnSettings = useCallbackRef((api: GridApi, column: AgGridColumn) => {
    const dynamics = getCustomColDefProperties(column.getColDef())?.dynamics;
    if (
      !isColumnDynamicsForSpec<PriceGridLegMarketDataColumnSettings>(
        dynamics,
        PRICE_GRID_LEG_MARKET_DATA_COLUMN_DYNAMICS_SPEC
      )
    ) {
      return;
    }

    const colId = column.getColId();

    legSettingsDialog.open({
      currentSettings: getColumnDynamicsState(dynamics),
      save: newSettings => {
        savePriceGridLegMarketDataColumnSettings({
          api,
          colId,
          dynamics,
          newSettings,
          getColumn: getLegColumn,
          getColDefOverlay: getLegMarketDataColDefOverlay,
        });
      },
    });
  });

  const openSettings = useCallbackRef(
    (
      currentSettings: PriceGridMarketDataColumnSettings | undefined,
      save: (newSettings: PriceGridMarketDataColumnSettings | undefined) => void
    ) => settingsDialog.open({ currentSettings, save })
  );

  const addPriceGridMarketDataColumn = useCallbackRef((api: GridApi) => {
    const priceColumnCount = countPriceGridMarketDataColumns(api.getColumnDefs()) + 1;
    const initialSettings: PriceGridMarketDataColumnSettings = {
      title: getNextPriceGridMarketDataColumnTitle(api.getColumnDefs()),
      field: 'security.Symbol',
    };
    const dynamics: ColumnDynamics<PriceGridMarketDataColumnSettings> = {
      colID: uuid(),
      spec: PRICE_GRID_MARKET_DATA_COLUMN_DYNAMICS_SPEC,
      removable: true,
      initialState: initialSettings,
      state: initialSettings,
    };
    settingsDialog.open({
      currentSettings: getColumnDynamicsState(dynamics),
      save: newSettings => {
        addPriceGridMarketDataColumnBeforeAddButton({
          api,
          dynamics,
          newSettings,
          getColumn,
          getColDefOverlay: getMarketDataColDefOverlay,
        });
        mixpanel.track(MixpanelEvent.AddPriceGridPriceColumn, {
          [MixpanelEventProperty.Count]: priceColumnCount,
        });
      },
    });
  });

  const openLegSettings = useCallbackRef(
    (
      currentSettings: PriceGridLegMarketDataColumnSettings | undefined,
      save: (newSettings: PriceGridLegMarketDataColumnSettings | undefined) => void
    ) => legSettingsDialog.open({ currentSettings, save })
  );

  const getMarketDataColDefOverlay = useCallback(
    (dynamics: ColumnDynamics<PriceGridMarketDataColumnSettings>) => {
      const settings = getColumnDynamicsState(dynamics);
      return buildPriceGridMarketDataColDefOverlay({
        settingsButton: getHeaderSettingsButton(settings?.marketSelectorValue),
        onSettingsClick: params => {
          const column = getHeaderGroupSettingsColumn(params);
          if (column) {
            openColumnSettings(params.api, column, params.columnGroup.getProvidedColumnGroup().getGroupId());
          }
        },
      });
    },
    [getHeaderSettingsButton, openColumnSettings]
  );

  const getLegMarketDataColDefOverlay = useCallback(
    (dynamics: ColumnDynamics<PriceGridLegMarketDataColumnSettings>) => {
      const settings = getColumnDynamicsState(dynamics);
      return buildPriceGridMarketDataColDefOverlay({
        settingsButton: getHeaderSettingsButton(settings?.marketSelectorValue),
        onSettingsClick: params => {
          const column = getHeaderGroupSettingsColumn(params);
          if (column) {
            openLegColumnSettings(params.api, column);
          }
        },
      });
    },
    [getHeaderSettingsButton, openLegColumnSettings]
  );

  const columnDynamicsSpecs = useMemo(
    () => [
      {
        label: 'Price Column',
        type: PRICE_GRID_MARKET_DATA_COLUMN_DYNAMICS_SPEC,
        getColumn,
        getColDefOverlay: getMarketDataColDefOverlay,
        openSettingsOnCreation: true,
        openSettings,
        getInitialSettings: () => ({
          title: 'Price',
          field: 'security.Symbol',
        }),
      },
      // Leg columns only make sense for multileg securities, so only variants that opt in should offer this.
      ...(supportsLegColumn
        ? [
            {
              label: 'Leg Price Column',
              type: PRICE_GRID_LEG_MARKET_DATA_COLUMN_DYNAMICS_SPEC,
              getColumn: getLegColumn,
              getColDefOverlay: getLegMarketDataColDefOverlay,
              openSettingsOnCreation: true,
              openSettings: openLegSettings,
              getInitialSettings: () => ({
                title: 'Leg Price',
              }),
            },
          ]
        : []),
    ],
    [
      getColumn,
      getLegColumn,
      getLegMarketDataColDefOverlay,
      getMarketDataColDefOverlay,
      openLegSettings,
      openSettings,
      supportsLegColumn,
    ]
  );

  return {
    columnDynamicsSpecs,
    addPriceGridMarketDataColumn,
    dialogs: (
      <>
        <PriceGridMarketDataColumnSettingsDialog
          {...settingsDialog}
          aggregationPresets={aggregationPresets ?? EMPTY_ARRAY}
          marketSelectorItems={marketSelectorItems}
        />
        <PriceGridLegMarketDataColumnSettingsDialog
          {...legSettingsDialog}
          aggregationPresets={aggregationPresets ?? EMPTY_ARRAY}
          marketSelectorItems={marketSelectorItems}
        />
      </>
    ),
  };
}
